import { Fragment, useState, useEffect, useContext } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentTextIcon, BuildingOfficeIcon, MapPinIcon, PhoneIcon, PlusIcon, TrashIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import { colorMap } from '../../../Common'
import { AuthContextApi } from '../../../Context/api'

const ModalCliente = ({ states, setters, actionCreate, actionEdit, color = 'green', getCep }) => {
    const colorClasses = colorMap[color] || colorMap.green
    const { extrairDadosCNPJ } = useContext(AuthContextApi)

    const [cnpj, setCnpj] = useState('')
    const [nomeFantasia, setNomeFantasia] = useState('')
    const [logradouro, setLogradouro] = useState('')
    const [numero, setNumero] = useState('')
    const [complemento, setComplemento] = useState('')
    const [cep, setCep] = useState('')
    const [bairro, setBairro] = useState('')
    const [municipio, setMunicipio] = useState('')
    const [uf, setUf] = useState('')
    const [enderecoEletronico, setEnderecoEletronico] = useState('')
    const [telefone, setTelefone] = useState('')

    const [searchingCep, setSearchingCep] = useState(false)


    const [errors, setErrors] = useState({})
    const [extracting, setExtracting] = useState(false)
    const [showExtractor, setShowExtractor] = useState(false)
    const [selectedFile, setSelectedFile] = useState(null)

    const handleSearchCep = async () => {
        if (!cep || cep.length < 8) {
            return
        }
        setSearchingCep(true)
        const data = await getCep(cep.replace(/\D/g, ''))
        setSearchingCep(false)
        if (data) {
            setLogradouro(data.logradouro || '')
            setBairro(data.bairro || '')
            setMunicipio(data.localidade || '')
            setUf(data.uf || '')
            // complemento sometimes comes from cep, but user might want to edit it, so we can append or just leave it. 
            // The requirement says "completar os campos", usually implies filling empty ones or overwriting.
            // Let's overwrite or fill.
        }
    }


    const handleFileSelect = (e) => {
        const file = e.target.files[0]
        if (file) {
            setSelectedFile(file)
        }
    }

    const handleExtractCNPJ = async () => {
        if (!selectedFile) return

        setExtracting(true)
        const data = await extrairDadosCNPJ(selectedFile)
        setExtracting(false)

        if (data) {
            setCnpj(data.cnpj || '')
            setNomeFantasia(data.nome_fantasia || '')
            setLogradouro(data.logradouro || '')
            setNumero(data.numero || '')
            setComplemento(data.complemento || '')
            setCep(data.cep || '')
            setBairro(data.bairro || '')
            setMunicipio(data.municipio || '')
            setUf(data.uf || '')
            setEnderecoEletronico(data.endereco_eletronico || '')
            setTelefone(data.telefone || '')
            setSelectedFile(null)
            setShowExtractor(false)
        }
    }


    const validate = () => {
        const newErrors = {}

        if (!cnpj.trim()) newErrors.cnpj = 'CNPJ é obrigatório'
        if (!nomeFantasia.trim()) newErrors.nomeFantasia = 'Nome fantasia é obrigatório'
        if (!logradouro.trim()) newErrors.logradouro = 'Logradouro é obrigatório'
        if (!numero.trim()) newErrors.numero = 'Número é obrigatório'
        if (!cep.trim()) newErrors.cep = 'CEP é obrigatório'
        if (!bairro.trim()) newErrors.bairro = 'Bairro é obrigatório'
        if (!municipio.trim()) newErrors.municipio = 'Município é obrigatório'
        if (!uf.trim()) newErrors.uf = 'UF é obrigatória'
        if (!enderecoEletronico.trim()) newErrors.enderecoEletronico = 'Endereço eletrônico é obrigatório'
        if (!telefone.trim()) newErrors.telefone = 'Telefone é obrigatório'

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }


    const handleSubmit = async () => {
        if (!validate()) {
            return
        }

        const dataToSend = {
            cnpj,
            nome_fantasia: nomeFantasia,
            logradouro,
            numero,
            complemento,
            cep,
            bairro,
            municipio,
            uf,
            endereco_eletronico: enderecoEletronico,
            telefone
        }

        if (states.isEdit) {
            actionEdit(states.Cliente.id, dataToSend)
        } else {
            actionCreate(dataToSend)
        }
    }


    useEffect(() => {
        if (!states.modalCliente) {
            setCnpj('')
            setNomeFantasia('')
            setLogradouro('')
            setNumero('')
            setComplemento('')
            setCep('')
            setBairro('')
            setMunicipio('')
            setUf('')
            setEnderecoEletronico('')
            setTelefone('')
            setErrors({})
            setShowExtractor(false)
            setSelectedFile(null)
        } else {
            if (states.isEdit && states.Cliente) {
                const cliente = states.Cliente
                setCnpj(cliente.cnpj || '')
                setNomeFantasia(cliente.nome_fantasia || '')
                setLogradouro(cliente.logradouro || '')
                setNumero(cliente.numero || '')
                setComplemento(cliente.complemento || '')
                setCep(cliente.cep || '')
                setBairro(cliente.bairro || '')
                setMunicipio(cliente.municipio || '')
                setUf(cliente.uf || '')
                setEnderecoEletronico(cliente.endereco_eletronico || '')
                setTelefone(cliente.telefone || '')
            }
        }
    }, [states.modalCliente, states.isEdit, states.Cliente])


    return (
        <Transition.Root show={states.modalCliente} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalCliente(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                                        onClick={() => setters.setModalCliente(false)}
                                    >
                                        <span className="sr-only">Fechar</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className={`border-b ${states.isEdit ? colorClasses.border : 'border-gray-200'} pb-4`}>
                                    <div className={`inline-flex items-center justify-center w-full px-4 py-2 rounded-lg ${states.isEdit ? colorClasses.containerBg : 'bg-gray-50'} mb-3`}>
                                        <span className={`text-sm font-semibold ${states.isEdit ? colorClasses.textGradient : 'text-gray-700'}`}>
                                            {states.isEdit ? '✏️ Modo Edição' : '➕ Novo Cliente'}
                                        </span>
                                    </div>
                                    <h3 className={`text-2xl font-bold leading-7 text-center ${states.isEdit ? colorClasses.textGradient : 'text-gray-900'}`}>
                                        {states.isEdit ? 'Editar Cliente' : 'Novo Cliente'}
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500 text-center">
                                        {states.isEdit ? 'Atualize as informações do cliente abaixo' : 'Preencha os dados para criar um novo cliente'}
                                    </p>
                                </div>

                                {/*!states.isEdit && (
                                    <div className="mt-6 border-b border-gray-200 pb-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <CloudArrowUpIcon className="h-5 w-5 text-green-600" />
                                                <h4 className="text-base font-semibold text-gray-900">Extração Automática de Dados</h4>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setShowExtractor(!showExtractor)}
                                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${showExtractor ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                                            >
                                                {showExtractor ? (
                                                    <>
                                                        <XMarkIcon className="w-4 h-4 mr-1" />
                                                        Fechar Extrator
                                                    </>
                                                ) : (
                                                    <>
                                                        <CloudArrowUpIcon className="w-4 h-4 mr-1" />
                                                        Extrair do Cartão CNPJ
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {showExtractor && (
                                            <div className="space-y-4">
                                                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-green-300 border-dashed rounded-lg cursor-pointer bg-green-50 hover:bg-green-100 transition-colors">
                                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                        <CloudArrowUpIcon className="w-10 h-10 mb-2 text-green-600" />
                                                        {selectedFile ? (
                                                            <>
                                                                <p className="mb-1 text-sm text-green-700 font-semibold">
                                                                    ✓ {selectedFile.name}
                                                                </p>
                                                                <p className="text-xs text-gray-500">Arquivo selecionado</p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="mb-1 text-sm text-gray-700">
                                                                    <span className="font-semibold">Selecione o Cartão CNPJ</span>
                                                                </p>
                                                                <p className="text-xs text-gray-500">PDF, PNG, JPG (MAX. 10MB)</p>
                                                            </>
                                                        )}
                                                    </div>
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*,.pdf"
                                                        onChange={handleFileSelect}
                                                        disabled={extracting}
                                                    />
                                                </label>

                                                {selectedFile && (
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleExtractCNPJ}
                                                            disabled={extracting}
                                                            className="flex-1 inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            {extracting ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                                    Extraindo dados...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                                                                    Extrair Dados do Cartão CNPJ
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedFile(null)}
                                                            disabled={extracting}
                                                            className="px-4 py-3 text-sm font-semibold text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            <XMarkIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) */}

                                <div className="mt-6 space-y-8 max-h-[60vh] overflow-y-auto pr-2">
                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <BuildingOfficeIcon className="h-5 w-5 text-green-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações da Empresa</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="cnpj" className="block text-sm font-medium leading-6 text-gray-900">
                                                    CNPJ <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="cnpj"
                                                    value={cnpj}
                                                    onChange={(e) => {
                                                        setCnpj(e.target.value)
                                                        if (errors.cnpj) setErrors({ ...errors, cnpj: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.cnpj ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="00.000.000/0000-00"
                                                />
                                                {errors.cnpj && <p className="mt-1 text-sm text-red-600">{errors.cnpj}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="nomeFantasia" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Nome Fantasia <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="nomeFantasia"
                                                    value={nomeFantasia}
                                                    onChange={(e) => {
                                                        setNomeFantasia(e.target.value)
                                                        if (errors.nomeFantasia) setErrors({ ...errors, nomeFantasia: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.nomeFantasia ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Nome fantasia"
                                                />
                                                {errors.nomeFantasia && <p className="mt-1 text-sm text-red-600">{errors.nomeFantasia}</p>}
                                            </div>
                                        </div>

                                    </div>

                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <MapPinIcon className="h-5 w-5 text-green-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Endereço</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div className="sm:col-span-2">
                                                <label htmlFor="cep" className="block text-sm font-medium leading-6 text-gray-900">
                                                    CEP <span className="text-red-500">*</span>
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        id="cep"
                                                        value={cep}
                                                        onChange={(e) => {
                                                            setCep(e.target.value)
                                                            if (errors.cep) setErrors({ ...errors, cep: null })
                                                        }}
                                                        className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.cep ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                        placeholder="00000-000"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleSearchCep}
                                                        disabled={searchingCep}
                                                        className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                                    >
                                                        {searchingCep ? '...' : <MapPinIcon className="h-5 w-5" />}
                                                    </button>
                                                </div>
                                                {errors.cep && <p className="mt-1 text-sm text-red-600">{errors.cep}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="numero" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Número <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="numero"
                                                    value={numero}
                                                    onChange={(e) => {
                                                        setNumero(e.target.value)
                                                        if (errors.numero) setErrors({ ...errors, numero: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.numero ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Número"
                                                />
                                                {errors.numero && <p className="mt-1 text-sm text-red-600">{errors.numero}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="complemento" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Complemento <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="complemento"
                                                    value={complemento}
                                                    onChange={(e) => {
                                                        setComplemento(e.target.value)
                                                        if (errors.complemento) setErrors({ ...errors, complemento: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.complemento ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Sala, andar, etc."
                                                />
                                                {errors.complemento && <p className="mt-1 text-sm text-red-600">{errors.complemento}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="logradouro" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Logradouro <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="logradouro"
                                                    value={logradouro}
                                                    onChange={(e) => {
                                                        setLogradouro(e.target.value)
                                                        if (errors.logradouro) setErrors({ ...errors, logradouro: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.logradouro ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Rua, Avenida, etc."
                                                />
                                                {errors.logradouro && <p className="mt-1 text-sm text-red-600">{errors.logradouro}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="bairro" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Bairro <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="bairro"
                                                    value={bairro}
                                                    onChange={(e) => {
                                                        setBairro(e.target.value)
                                                        if (errors.bairro) setErrors({ ...errors, bairro: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.bairro ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Bairro"
                                                />
                                                {errors.bairro && <p className="mt-1 text-sm text-red-600">{errors.bairro}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="municipio" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Município <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="municipio"
                                                    value={municipio}
                                                    onChange={(e) => {
                                                        setMunicipio(e.target.value)
                                                        if (errors.municipio) setErrors({ ...errors, municipio: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.municipio ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="Município"
                                                />
                                                {errors.municipio && <p className="mt-1 text-sm text-red-600">{errors.municipio}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="uf" className="block text-sm font-medium leading-6 text-gray-900">
                                                    UF <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="uf"
                                                    value={uf}
                                                    onChange={(e) => {
                                                        setUf(e.target.value)
                                                        if (errors.uf) setErrors({ ...errors, uf: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.uf ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="UF"
                                                    maxLength={2}
                                                />
                                                {errors.uf && <p className="mt-1 text-sm text-red-600">{errors.uf}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <PhoneIcon className="h-5 w-5 text-green-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Contato e Inscrições</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="enderecoEletronico" className="block text-sm font-medium leading-6 text-gray-900">
                                                    E-mail <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    id="enderecoEletronico"
                                                    value={enderecoEletronico}
                                                    onChange={(e) => {
                                                        setEnderecoEletronico(e.target.value)
                                                        if (errors.enderecoEletronico) setErrors({ ...errors, enderecoEletronico: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.enderecoEletronico ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="email@exemplo.com"
                                                />
                                                {errors.enderecoEletronico && <p className="mt-1 text-sm text-red-600">{errors.enderecoEletronico}</p>}
                                            </div>

                                            <div>
                                                <label htmlFor="telefone" className="block text-sm font-medium leading-6 text-gray-900">
                                                    Telefone <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="telefone"
                                                    value={telefone}
                                                    onChange={(e) => {
                                                        setTelefone(e.target.value)
                                                        if (errors.telefone) setErrors({ ...errors, telefone: null })
                                                    }}
                                                    className={`mt-2 block w-full rounded-lg border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.telefone ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-green-600'} placeholder:text-gray-400 sm:text-sm sm:leading-6 transition-all`}
                                                    placeholder="(00) 00000-0000"
                                                />
                                                {errors.telefone && <p className="mt-1 text-sm text-red-600">{errors.telefone}</p>}
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className="mt-8 flex items-center justify-end gap-x-4 border-t border-gray-200 pt-6">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalCliente(false)}
                                        className="rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm ${colorClasses.headerButtonBg} ${colorClasses.headerButtonHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${colorClasses.focusRingOffset} transition-all`}
                                    >
                                        {states.isEdit ? 'Salvar Alterações' : 'Criar Cliente'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalCliente

